import { z } from 'zod';
/**
 * autodev_review_v1 — structured PR review output produced by the AutoDev
 * reviewer profile (Layer 2 quality gate, roadmap #165).
 *
 * The reviewer LLM is asked to answer two evidence-bearing questions about
 * a diff:
 *
 *   1. For every newly introduced state/ref/callback/import, is there a real
 *      consumer in the same file (JSX render, ref={...}, onX={...}, ...)?
 *   2. If a paradigm is applied across multiple files (e.g. smart-scroll
 *      widget + page), are all four pieces wired in each file (state +
 *      handler + JSX binding + UI feedback)?
 *
 * The validator (`packages/api/src/modules/autodev/reviewers/validator.ts`)
 * consumes this object and decides block/pass.
 */
export const unboundIdentifierKindSchema = z.enum([
    'state',
    'ref',
    'callback',
    'import',
    'other',
]);
export const unboundIdentifierSchema = z.object({
    file: z.string().min(1),
    line: z.number().int().nonnegative(),
    name: z.string().min(1),
    kind: unboundIdentifierKindSchema,
    expected_consumer: z.string().min(1),
    evidence: z.string().min(1),
});
export const paradigmFileSchema = z.object({
    path: z.string().min(1),
    pieces_present: z.array(z.string()),
    pieces_missing: z.array(z.string()),
});
export const multiFileParadigmMismatchSchema = z.object({
    paradigm: z.string().min(1),
    files: z.array(paradigmFileSchema).min(1),
    severity: z.enum(['block', 'warn']),
});
export const issueIntentCoverageSchema = z.object({
    issue_text_excerpt: z.string(),
    expected_surfaces: z.array(z.string()),
    diff_touches: z.array(z.string()),
    surfaces_missing: z.array(z.string()),
    coverage_score: z.number().min(0).max(1),
});
export const autodevReviewMetaSchema = z.object({
    pr_number: z.number().int().positive(),
    head_sha: z.string().min(1),
    profile_id: z.number().int().positive(),
    reviewed_at: z.string().datetime({ offset: true }).or(z.string().datetime()),
    schema_version: z.literal('autodev_review_v1'),
});
export const autodevReviewV1Schema = z.object({
    unbound_identifiers: z.array(unboundIdentifierSchema),
    multi_file_paradigm_mismatch: z.array(multiFileParadigmMismatchSchema),
    issue_intent_coverage: issueIntentCoverageSchema,
    meta: autodevReviewMetaSchema,
});
//# sourceMappingURL=autodevReview.schema.js.map